import React, { useState, useEffect } from "react";

import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";

import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { RootState } from "../../store";

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize sidebar state: open on desktop (md+), closed on mobile
  // Using a function to ensure the initial state is set correctly
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const selectedService = useSelector((state: RootState) => state.service.selectedService);

  // Update sidebar state when screen size changes
  // On mobile: close sidebar when switching from desktop to mobile
  // On desktop: open sidebar when switching from mobile to desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Get title from location or use service name
  const getTitle = () => {
    if (selectedService?.serviceName) {
      return `Hello ${selectedService.serviceName}!`;
    }
    return "Hello!";
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} isMobile={isMobile} />
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0, // Prevent flex item from overflowing
          marginLeft: { xs: 0, md: sidebarOpen ? 0 : 0 },
          boxSizing: "border-box",
        }}
      >
        <Header onSidebarToggle={handleSidebarToggle} title={getTitle()} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            padding: { xs: 2, sm: 2.5, md: 3 },
            backgroundColor: "#f5f5f5",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "hidden", // Prevent horizontal overflow
            overflowY: "auto", // Allow vertical scrolling
          }}
        >
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};
